// CustomSelect.tsx
import * as Select from '@radix-ui/react-select';
import styled from '@emotion/styled';
import { FieldWrapper, Label, Control } from '../../components/text-field/text-field';
import { FaChevronDown } from 'react-icons/fa';

/* ───────── styled parts ───────── */

const Trigger = styled(Select.Trigger)`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px;
  border: 1px solid #2b2d33;
  border-radius: 9.42px;
  background: #23252a;
  cursor: pointer;
  font-family: Inter;
  font-weight: 500;
  font-size: 15px;
  color: #e9ecf5;

  &:focus {
    border-color: #7a8ae6;
  }

  /* ↓ Radix adds data-disabled when disabled={true} */
  &[data-disabled] {
    cursor: not-allowed;
    background: #f6f7fb;
    color: #9aa0c7;
    border-color: #d5d9ee;
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  margin-left: 8px;
  opacity: 0.6;
  color: #b7bdcb;
`;

const Content = styled(Select.Content)`
  overflow: hidden;
  background: #23252a;
  border: 1px solid #2b2d33;
  border-radius: 9.42px;
  margin-top: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  min-width: var(--radix-select-trigger-width);
`;

const Viewport = styled(Select.Viewport)`
  display: flex;
  flex-direction: column;
  padding: 4px 0;
`;

const Item = styled(Select.Item)`
  all: unset;
  box-sizing: border-box;
  height: 48px;
  line-height: 48px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  cursor: pointer;

  font-family: Inter;
  font-weight: 500;
  font-size: 15px;
  font-style: normal;
  letter-spacing: 0px;
  vertical-align: middle;
  leading-trim: none;
  color: #e9ecf5;

  &[data-highlighted] {
    background-color: #2b2d33;
  }

  &[aria-selected='true'] {
    font-weight: 600;
  }
`;

/* ───────── component API ───────── */

export interface CustomSelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  id: string;
  label?: string;
  value: string;
  onChange?: (v: string) => void;
  options: CustomSelectOption[];
  disabled?: boolean;
}

/* CustomSelect.tsx — SelectField component */
export const SelectField: React.FC<SelectFieldProps> = ({
  id,
  label,
  value,
  onChange = () => {},
  options,
  disabled = false,
}) => {
  /* 🔎 derive the human-readable label once */
  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;

  return (
    <FieldWrapper>
      {label && <Label htmlFor={id}>{label}</Label>}

      <Control style={{ fontSize: '16px' }}>
        <Select.Root value={value} onValueChange={onChange} disabled={disabled}>
          <Select.Trigger id={id} asChild disabled={disabled}>
            <Trigger
              {...(disabled ? { 'data-disabled': '' } : {})}
              style={{ '--radix-select-trigger-width': '100%', fontSize: '16px' } as any}
            >
              {/* 👉 explicit label so it appears even if item list is missing */}
              <Select.Value aria-label={selectedLabel}>{selectedLabel}</Select.Value>

              <IconWrapper>
                <FaChevronDown size={16} />
              </IconWrapper>
            </Trigger>
          </Select.Trigger>

          {/* keep menu out of the DOM when disabled */}
          {!disabled && (
            <Select.Portal container={document.getElementById('root') ?? undefined}>
              <Content>
                <Viewport>
                  {options.map((opt) => (
                    <Item key={opt.value} value={opt.value}>
                      <Select.ItemText>{opt.label}</Select.ItemText>
                    </Item>
                  ))}
                </Viewport>
              </Content>
            </Select.Portal>
          )}
        </Select.Root>
      </Control>
    </FieldWrapper>
  );
};
